import React, { useState } from 'react';
import { InvitationDesign, TextStyle } from '../../../_types/invitation-design-v2';
import { HexColorPicker } from 'react-colorful';
import FontSelector from '../FontSelector';
import { AVAILABLE_FONTS } from '../../../_types/invitation-design';

interface StylesTabProps {
  design: InvitationDesign;
  onStylePresetUpdate: (preset: string, updates: Partial<TextStyle>) => void;
  onThemeUpdate: (updates: Partial<typeof design.template.theme>) => void;
}

const StylesTab: React.FC<StylesTabProps> = ({ design, onStylePresetUpdate, onThemeUpdate }) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('title');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  
  const stylePresets = design.template.stylePresets || {};
  const currentPreset = stylePresets[selectedPreset] || {};
  const theme = design.template.theme;
  
  // Helper to resolve template variables to actual values
  const resolveTemplateVariable = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    
    // Replace template variables with actual theme values
    return value
      .replace('{{template.theme.primaryColor}}', theme.primaryColor || '')
      .replace('{{template.theme.secondaryColor}}', theme.secondaryColor || '')
      .replace('{{template.theme.textColor}}', theme.textColor || '')
      .replace('{{template.theme.accentColor}}', theme.accentColor || '')
      .replace('{{template.theme.fontFamily}}', theme.fontFamily || '')
      .replace('{{template.theme.secondaryFontFamily}}', theme.secondaryFontFamily || '');
  };
  
  // Get the actual color value (resolved from template if needed)
  const getActualColor = (): string => {
    const colorValue = currentPreset.color || theme.textColor;
    return resolveTemplateVariable(colorValue);
  };
  
  // Click outside handler
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.color-dropdown-container')) {
        setShowColorDropdown(false);
        setShowColorPicker(false);
      }
    };
    
    if (showColorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColorDropdown]);
  
  
  const fontWeights = [
    { name: 'Light', value: '300' },
    { name: 'Normal', value: 'normal' },
    { name: 'Medium', value: '500' },
    { name: 'Semibold', value: '600' },
    { name: 'Bold', value: 'bold' },
    { name: 'Black', value: '900' },
  ];

  const updatePresetStyle = (updates: Partial<TextStyle>) => {
    onStylePresetUpdate(selectedPreset, updates);
  };

  return (
    <div className="space-y-4">
      {/* Preset Selector */}
      <div>
        <h3 className="text-xs font-medium text-gray-700 mb-2">Style Preset</h3>
        <div className="grid grid-cols-2 gap-1">
          {Object.keys(stylePresets).map((preset) => (
            <button
              key={preset}
              onClick={() => setSelectedPreset(preset)}
              className={`px-3 py-2 rounded-md border transition-all text-left ${
                selectedPreset === preset
                  ? 'border-rose-500 bg-rose-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-xs font-medium capitalize">{preset}</div>
              <div className="text-xs text-gray-500">
                {stylePresets[preset].fontSize}px
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Style Configuration */}
      <div className="border-t pt-3">
        <h3 className="text-xs font-medium text-gray-700 mb-3">
          Configure "{selectedPreset}" Style
        </h3>

        {/* Font Family */}
        <div className="mb-3">
          <FontSelector
            label="Font Family"
            value={(() => {
              const fontValue = currentPreset.fontFamily || '{{template.theme.fontFamily}}';
              // If it's a template variable, return it as-is
              if (fontValue.includes('{{template.theme.')) {
                return fontValue;
              }
              // Otherwise extract just the font name
              return fontValue.split(',')[0].trim();
            })()}
            onChange={(fontFamily) => {
              // Check if it's a template variable
              if (fontFamily.includes('{{template.theme.')) {
                updatePresetStyle({ fontFamily });
              } else {
                // Get the full font family value from AVAILABLE_FONTS
                const fonts = Object.values(AVAILABLE_FONTS);
                const selectedFont = fonts.find(f => f.family === fontFamily);
                if (selectedFont) {
                  updatePresetStyle({ 
                    fontFamily: `${selectedFont.family}, ${selectedFont.category}` 
                  });
                }
              }
            }}
            placeholder="Choose a font..."
            themeFonts={{
              primary: theme.fontFamily,
              secondary: theme.secondaryFontFamily
            }}
          />
        </div>

        {/* Font Size */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Font Size</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={currentPreset.fontSize || 16}
              onChange={(e) => updatePresetStyle({ fontSize: Number(e.target.value) })}
              min="8"
              max="72"
              step="1"
              className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <div className="w-12 text-center">
              <input
                type="number"
                value={currentPreset.fontSize || 16}
                onChange={(e) => updatePresetStyle({ fontSize: Number(e.target.value) })}
                className="w-full px-1 py-0.5 text-xs text-center border border-gray-300 rounded"
                min="8"
                max="120"
              />
            </div>
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>

        {/* Font Weight */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Font Weight</label>
          <select
            value={currentPreset.fontWeight || 'normal'}
            onChange={(e) => updatePresetStyle({ fontWeight: e.target.value })}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-rose-500 focus:border-transparent"
          >
            {fontWeights.map((weight) => (
              <option key={weight.value} value={weight.value}>
                {weight.name}
              </option>
            ))}
          </select>
        </div>

        {/* Font Style Options */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Font Style</label>
          <div className="flex gap-1">
            <button
              onClick={() => updatePresetStyle({ 
                fontStyle: currentPreset.fontStyle === 'italic' ? 'normal' : 'italic' 
              })}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                currentPreset.fontStyle === 'italic'
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <span className="italic">Italic</span>
            </button>
            <button
              onClick={() => updatePresetStyle({ 
                textTransform: currentPreset.textTransform === 'uppercase' ? 'none' : 'uppercase' 
              })}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                currentPreset.textTransform === 'uppercase'
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              UPPERCASE
            </button>
          </div>
        </div>

        {/* Line Height */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Line Height</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={currentPreset.lineHeight || 1.6}
              onChange={(e) => updatePresetStyle({ lineHeight: Number(e.target.value) })}
              min="0.8"
              max="3"
              step="0.1"
              className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <span className="text-xs text-gray-500 w-8">{(currentPreset.lineHeight || 1.6).toFixed(1)}</span>
          </div>
        </div>

        {/* Letter Spacing */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Letter Spacing</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={currentPreset.letterSpacing || 0}
              onChange={(e) => updatePresetStyle({ letterSpacing: Number(e.target.value) })}
              min="-5"
              max="10"
              step="0.5"
              className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <span className="text-xs text-gray-500 w-10">{currentPreset.letterSpacing || 0}px</span>
          </div>
        </div>

        {/* Word Spacing */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Word Spacing</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={currentPreset.wordSpacing || 0}
              onChange={(e) => updatePresetStyle({ wordSpacing: Number(e.target.value) })}
              min="-5"
              max="20"
              step="1"
              className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <span className="text-xs text-gray-500 w-10">{currentPreset.wordSpacing || 0}px</span>
          </div>
        </div>

        {/* Text Alignment */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Text Alignment</label>
          <div className="flex gap-1">
            {['left', 'center', 'right'].map((align) => (
              <button
                key={align}
                onClick={() => updatePresetStyle({ textAlign: align as any })}
                className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                  (currentPreset.textAlign || 'center') === align
                    ? 'bg-rose-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {align.charAt(0).toUpperCase() + align.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Margins */}
        <div className="mb-3">
          <label className="text-xs text-gray-600 mb-1 block">Bottom Margin</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={currentPreset.marginBottom || 0}
              onChange={(e) => updatePresetStyle({ marginBottom: Number(e.target.value) })}
              min="0"
              max="100"
              step="1"
              className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <span className="text-xs text-gray-500 w-10">{currentPreset.marginBottom || 0}px</span>
          </div>
        </div>

        {/* Color */}
        <div className="relative color-dropdown-container">
          <label className="text-xs text-gray-600 mb-1 block">Color</label>
          <button
            onClick={() => setShowColorDropdown(!showColorDropdown)}
            className="w-full flex items-center justify-between p-2 border border-gray-300 rounded hover:border-gray-400 bg-white"
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-6 h-6 rounded border border-gray-300"
                style={{ backgroundColor: getActualColor() }}
              />
              <span className="text-xs">
                {currentPreset.color || 'Theme Default'}
              </span>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform ${showColorDropdown ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Color Dropdown */}
          {showColorDropdown && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
              {/* Theme Colors */}
              <div className="p-3">
                <div className="text-xs text-gray-500 font-medium mb-2">Theme Colors</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      updatePresetStyle({ color: undefined });
                      setShowColorDropdown(false);
                    }}
                    className={`flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition-colors ${
                      currentPreset.color === undefined ? 'bg-rose-50' : ''
                    }`}
                  >
                    <div 
                      className="w-6 h-6 rounded border border-gray-300" 
                      style={{ backgroundColor: theme.textColor }}
                    />
                    <span className="text-sm">Default</span>
                  </button>
                  {[
                    { name: 'Primary', value: '{{template.theme.primaryColor}}', color: theme.primaryColor },
                    { name: 'Secondary', value: '{{template.theme.secondaryColor}}', color: theme.secondaryColor },
                    { name: 'Text', value: '{{template.theme.textColor}}', color: theme.textColor },
                    { name: 'Accent', value: '{{template.theme.accentColor}}', color: theme.accentColor },
                  ].map((themeColor) => (
                    <button
                      key={themeColor.value}
                      onClick={() => {
                        updatePresetStyle({ color: themeColor.value });
                        setShowColorDropdown(false);
                      }}
                      className={`flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition-colors ${
                        currentPreset.color === themeColor.value ? 'bg-rose-50' : ''
                      }`}
                    >
                      <div 
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: themeColor.color }}
                      />
                      <span className="text-sm">{themeColor.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-gray-100" />
              
              {/* Custom Color Option */}
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                className={`w-full px-3 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                  showColorPicker ? 'bg-rose-50' : ''
                }`}
              >
                <div className="w-6 h-6 rounded border-2 border-gray-300 bg-gradient-to-br from-red-500 via-green-500 to-blue-500" />
                <span className="text-sm font-medium">Custom Color</span>
              </button>
              
              {/* Color Picker */}
              {showColorPicker && (
                <div className="p-4 border-t border-gray-100">
                  <HexColorPicker
                    color={getActualColor()}
                    onChange={(color) => updatePresetStyle({ color })}
                  />
                  <button
                    onClick={() => {
                      setShowColorPicker(false);
                      setShowColorDropdown(false);
                    }}
                    className="w-full mt-3 px-4 py-2 bg-rose-500 text-white rounded text-sm font-medium hover:bg-rose-600"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="border-t pt-3">
        <h4 className="text-xs font-medium text-gray-700 mb-2">Preview</h4>
        <div className="p-3 bg-gray-50 rounded text-sm">
          <div
            style={{
              fontFamily: currentPreset.fontFamily?.replace(/\{\{.*?\}\}/g, theme.fontFamily) || theme.fontFamily,
              fontSize: `${currentPreset.fontSize || 16}px`,
              fontWeight: currentPreset.fontWeight || 'normal',
              fontStyle: currentPreset.fontStyle || 'normal',
              textTransform: currentPreset.textTransform || 'none',
              lineHeight: currentPreset.lineHeight || 1.6,
              letterSpacing: `${currentPreset.letterSpacing || 0}px`,
              wordSpacing: `${currentPreset.wordSpacing || 0}px`,
              textAlign: (currentPreset.textAlign || 'center') as any,
              color: currentPreset.color?.replace(/\{\{.*?\}\}/g, theme.textColor) || theme.textColor,
            }}
          >
            The quick brown fox jumps over the lazy dog
          </div>
        </div>
      </div>
    </div>
  );
};

export default StylesTab;